/**
 * <SamWorkCheckWait> — Sam lit la photo du travail (molecule).
 *
 * L'élève vient d'envoyer une photo par « Où j'en suis ». La lecture dure plusieurs secondes
 * (l'envoi, puis l'analyse) : trois points ne suffisent pas, ils sont calibrés pour deux
 * secondes et laissent croire à une panne. L'attente se NOMME, dans la bulle de Sam, et la
 * réponse à venir se dessine dessous. Au-delà de neuf secondes (`late`), Sam reconnaît que
 * c'est long.
 *
 * C'est la suite d'un geste de l'élève, dite en mots : pas un contenu qui charge, donc pas une
 * roue. Les lignes squelettes passent par `<DelayedSkeleton>`, comme partout.
 *
 * La carte de l'ancien code était teintée au bleu de Sam ; ici c'est sa bulle, sur la surface
 * de toutes ses bulles. C'est l'écran qui compte les neuf secondes.
 *
 * Composes DS pieces only — `<MessageRow>`, `<Icon>`, `<Text>`, `<TypingDots>`, `<Skeleton>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { TypingDots } from "../../atoms/TypingDots"
import { Text } from "../../atoms/typography/Text"
import { MessageRow } from "../MessageRow"

export type SamWorkCheckWaitProps = {
    /** La lecture dépasse neuf secondes : Sam le dit. Défaut `false`. */
    late?: boolean
    testID?: string
}

const TEXTS = {
    reading: "Je regarde ton travail…",
    // L'ancien code liait les deux phrases par un tiret cadratin.
    late: "C'est un peu plus long que d'habitude. Je regarde encore, ne quitte pas.",
}

const WAIT_MESSAGE = { id: "sam-work-check", author: "other" } as const

/** La réponse à venir : trois lignes, de plus en plus courtes. En px : la bulle se cale dessus. */
const ANSWER_LINES = [212, 176, 124]

export function SamWorkCheckWait(props: SamWorkCheckWaitProps) {
    const { late = false, testID } = props

    return (
        <View
            accessible
            accessibilityRole="text"
            accessibilityLabel={late ? `${TEXTS.reading} ${TEXTS.late}` : TEXTS.reading}
            accessibilityLiveRegion="polite"
        >
            <MessageRow message={WAIT_MESSAGE} speaker="agent" champion="sam" tail testID={testID}>
                <Stack gap="3">
                    <Row gap="2" align="center">
                        <Icon name="eye" size={16} color="text-muted" />
                        <Text size="text-md" weight="bold" style={styles.label}>
                            {TEXTS.reading}
                        </Text>
                        <TypingDots />
                    </Row>
                    <DelayedSkeleton>
                        <Stack gap="1">
                            {ANSWER_LINES.map((width) => (
                                <Skeleton key={width} textSize="text-md" width={width} />
                            ))}
                        </Stack>
                    </DelayedSkeleton>
                    {late && (
                        <Text size="text-sm" color="text-muted" style={styles.late}>
                            {TEXTS.late}
                        </Text>
                    )}
                </Stack>
            </MessageRow>
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    label: {
        flexShrink: 1,
    },
    /** La phrase ne s'étire pas plus large que la réponse qu'elle annonce. */
    late: {
        maxWidth: ANSWER_LINES[0],
    },
}))
